import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { LessonDTO } from 'src/app/model/lessonsDTO.model';

import { QuizDTO } from '@app/model/quizDTO.model';
import { HttpParams } from '@angular/common/http';
import { HttpService, interceptorsToken, BundleType, SettingsFeedbackField } from 'library-explorer';
import { QuizType } from 'src/app/model/enums/quiz-type.enum';
import { DataResponse } from '@app/model/data-response.model';

@Injectable({
  providedIn: 'root'
})
export class LessonService {
  constructor(
    private readonly httpService: HttpService
  ) { }

  public getLessons(moduleId: string = null, extraParams?: HttpParams): Observable<DataResponse<LessonDTO>> {
    let params = extraParams || new HttpParams();
    params = params
      .set('active', 'true')
      .set(interceptorsToken.HANDLE_PERMISSION_DENIED_PARAM, 'true');
    
    if (moduleId) {
      params = params.set('parentModule', moduleId);
    }

    return this.httpService.get<DataResponse<LessonDTO>>('lessons', params);
  }

  public getLessonByID<T = LessonDTO | QuizDTO>(id: string): Observable<T> {
    const params = new HttpParams()
      .set('active', 'true')
      .set('id', id)
      .set(interceptorsToken.HANDLE_PERMISSION_DENIED_PARAM, 'true');

    return this.httpService.get<T>('node', params);
  }

  public getAllLessons(
    moduleId: string = null,
    extraParams: HttpParams = null,
    lessons: LessonDTO[] = [],
    pagination: any = { itemsPerPage: 10, currentPage: 1, offset: 0 }
  ): Observable<LessonDTO[]> {
    let params = extraParams || new HttpParams();
    params = params
      .set('limit', pagination.itemsPerPage)
      .set('offset', pagination.offset);
    
    return this.getLessons(moduleId, params).pipe(
      switchMap(res => {
        lessons = [...lessons, ...res.items];
        
        if (res._meta.maxcount <= lessons.length) {
          return of(lessons);
        }
        
        pagination.currentPage++;
        pagination.offset = (pagination.currentPage - 1) * pagination.itemsPerPage;
        return this.getAllLessons(moduleId, params, lessons, pagination);
      })
    );
  }

  public sendFeedback(entityId: string, data: { rate: number, fields?: SettingsFeedbackField[]}): Observable<void> {
    const body = { entityId };

    if (data) {
      Object.assign(body, data);
    } else {
      Object.assign(body, { isCanceled: true });
    }

    return this.httpService.post<void>('feedback', body);
  }

  public startQuizOrLessonEntity(id: string): Observable<void> {
    return this.httpService.post('entity-start', { id });
  }

  public getLessonIndicatorBundle(lesson: LessonDTO & { quizType?: QuizType }): BundleType {
    if (lesson.hasOnlyWebinarSlide) {
      return BundleType.WEBINAR;
    }

    if (lesson.bundle === BundleType.QUIZ) {
      return lesson.quizType === QuizType.POLL_QUIZ ? BundleType.POLL_QUIZ : BundleType.QUIZ;
    }

    return BundleType.LESSON;
  }

}
